import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { H1, Table, LinkButton, AccessCheck } from "@components";
import { useAuth } from "features/auth";
import { UserRoleEnum, UserRole } from "features/users/types";
import { canCreateUser, canEditUser } from "features/users/utils";
import { useNotification } from "@hooks/useNotifications";
import type { GridComparatorFn } from "@mui/x-data-grid";
import { TableUserActionsCell } from "../components";
import { getRoleLabel, getRoleIndex } from "../utils";
import { useFetchUsers } from "../hooks/useFetchUsers";

const roleComparator: GridComparatorFn<UserRole> = (v1, v2) => getRoleIndex(v1) - getRoleIndex(v2);

export const UsersPage = () => {
  const [users] = useFetchUsers();
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const { showInfo } = useNotification();

  const renderCell = (params: any) => {
    const isCurrentUser = user.email === params.row.email;

    return <div style={isCurrentUser ? { fontWeight: "bold" } : undefined}>{params.value}</div>;
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        renderCell,
      },
      {
        field: "role",
        headerName: "Role",
        flex: 1,
        valueGetter: (value: number) => getRoleLabel(value),
        sortComparator: roleComparator,
        renderCell,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        renderCell,
      },
      {
        field: "actions",
        headerName: "Actions",
        renderCell: TableUserActionsCell,
        sortable: false,
      },
    ],
    [],
  );

  if (!users) {
    return null;
  }

  const onRowClick = (params: any) => {
    const hasAccess = canEditUser(user, params?.row.role, params?.row.id);

    if (hasAccess) {
      const link = `/users/${params?.row.email}/edit`;

      navigate(link);
    } else {
      showInfo("You don't have access to this user.");
    }
  };

  const hasAccessCreate = canCreateUser(userRole);
  return (
    <AccessCheck minAccessLevel={UserRoleEnum.Manager}>
      <H1 label="Users">{hasAccessCreate ? <LinkButton to="/users/create" label="Create New User" /> : null}</H1>
      <Table
        rows={users}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "role", sort: "asc" }],
          },
        }}
        onRowClick={onRowClick}
        storageId="users"
      />
    </AccessCheck>
  );
};
